import {
  type BaseQuote,
  type BrokerQuoteDetail,
  type CoverKey,
  type CoverType,
  type CoverTypePrice,
  type CustomerQuote,
  type Instalments,
  type LeadQuote,
  type PaymentType,
  type PriceInfo,
  type MarketplacePricingSummary,
  type ProviderSummary,
  type QuoteReference,
} from "@ir/types";
import { calculateCustomerPricing } from "./customerPricing";
import { getGraniteAnnualPremium, getProviderAnnualPremium } from "./granitePricing";

export const EXPECTED_MARKETPLACE_PROVIDERS = ["granite", "inshur", "kinetiq"] as const;

function round2(value: number | null | undefined): number {
  return Number((value ?? 0).toFixed(2));
}

function toCustomerCoverTypePrice(
  provider: string,
  annualPremium: number,
): CoverTypePrice {
  const annualPricing = calculateCustomerPricing(provider, annualPremium);
  const elevenMonthPricing = calculateCustomerPricing(provider, annualPremium, 11);
  const tenMonthPricing = calculateCustomerPricing(provider, annualPremium, 10);

  return {
    elevenMonths: {
      initialPayment: elevenMonthPricing.finance?.initialPayment ?? 0,
      monthlyPayment: elevenMonthPricing.finance?.monthlyPayment ?? 0,
      totalPayable: elevenMonthPricing.finance?.totalPayable ?? 0,
      customerPricing: elevenMonthPricing.finance,
    },
    tenMonths: {
      initialPayment: tenMonthPricing.finance?.initialPayment ?? 0,
      monthlyPayment: tenMonthPricing.finance?.monthlyPayment ?? 0,
      totalPayable: tenMonthPricing.finance?.totalPayable ?? 0,
      customerPricing: tenMonthPricing.finance,
    },
    annually: annualPricing.totalSellingPrice,
    rawAnnually: round2(annualPremium),
  };
}

function normaliseCoverType(value?: string | null): CoverType {
  switch (value) {
    case "Third Party":
      return "Third Party Only";
    case "Third Party Fire and Theft":
      return "Third Party, Fire and Theft";
    case "Third Party, Fire and Theft":
      return "Third Party, Fire and Theft";
    case "Third Party Only":
      return "Third Party Only";
    default:
      return "Comprehensive";
  }
}

function providerSortValue(provider?: string | null): number {
  return EXPECTED_MARKETPLACE_PROVIDERS.indexOf((provider ?? "").toLowerCase() as (typeof EXPECTED_MARKETPLACE_PROVIDERS)[number]);
}

export function hasCustomerFacingQuotes(baseQuote: BaseQuote): boolean {
  const cover = baseQuote.non_broker_quote_details?.cover;
  if (cover) {
    if (
      getProviderAnnualPremium(baseQuote.provider, cover.comprehensive) > 0 ||
      getProviderAnnualPremium(baseQuote.provider, cover.tpft) > 0 ||
      getProviderAnnualPremium(baseQuote.provider, cover.tp) > 0
    ) {
      return true;
    }
  }

  return (baseQuote.broker_quote_details ?? []).some(
    (detail) => !detail.is_decline && (detail.premium ?? 0) > 0,
  );
}

export function isTerminalWithoutQuotes(baseQuote: BaseQuote): boolean {
  const state = (baseQuote.state ?? "").trim().toLowerCase();
  return [
    "failed",
    "declined",
    "validation failed",
    "error",
    "no quote",
    "refer",
    "referred",
  ].includes(state);
}

function toProviderSummary(baseQuote: BaseQuote): ProviderSummary {
  const normalizedProvider = (baseQuote.provider ?? "unknown").toLowerCase();
  const hasQuotes = hasCustomerFacingQuotes(baseQuote);
  const resolved = hasQuotes || isTerminalWithoutQuotes(baseQuote);

  return {
    provider: baseQuote.provider ?? "unknown",
    state: baseQuote.state,
    quoteCount: mapBaseQuoteToCustomerQuotes(baseQuote).length,
    hasCustomerQuotes: hasQuotes,
    isResolved: resolved,
    isExpectedPricingProvider: EXPECTED_MARKETPLACE_PROVIDERS.includes(
      normalizedProvider as (typeof EXPECTED_MARKETPLACE_PROVIDERS)[number],
    ),
  };
}

export function deriveMarketplacePricingSummary(
  baseQuotes: BaseQuote[],
): MarketplacePricingSummary {
  const outcomes = EXPECTED_MARKETPLACE_PROVIDERS.map((provider) => {
    const baseQuote = baseQuotes.find(
      (quote) => quote.provider?.toLowerCase() === provider,
    );
    const hasQuotes = baseQuote ? hasCustomerFacingQuotes(baseQuote) : false;
    const terminalWithoutQuotes = baseQuote ? isTerminalWithoutQuotes(baseQuote) : false;

    return {
      provider,
      exists: Boolean(baseQuote),
      hasQuotes,
      isResolved: hasQuotes || terminalWithoutQuotes,
    };
  });

  const quotedProviderCount = outcomes.filter((outcome) => outcome.hasQuotes).length;
  const resolvedProviderCount = outcomes.filter((outcome) => outcome.isResolved).length;
  const missingProviders = outcomes
    .filter((outcome) => !outcome.exists)
    .map((outcome) => outcome.provider);
  const waitingProviders = outcomes
    .filter((outcome) => !outcome.isResolved)
    .map((outcome) => outcome.provider);
  const providersWithoutQuotes = outcomes
    .filter((outcome) => outcome.exists && !outcome.hasQuotes)
    .map((outcome) => outcome.provider);
  const providerBreakdown = outcomes.map((outcome) => ({
    provider: outcome.provider,
    returnedQuote: outcome.hasQuotes,
  }));
  const graniteHasResponded =
    outcomes.find((outcome) => outcome.provider === "granite")?.isResolved ?? false;
  const allExpectedProvidersResolved = waitingProviders.length === 0;
  const customerViewReady = graniteHasResponded && quotedProviderCount > 0;
  const ogiBaseQuote = baseQuotes.find(
    (quote) => quote.provider?.toLowerCase() === "opengi",
  );
  const ogiMetadata = ogiBaseQuote?.provider_metadata?.ogi;
  const ogiStatus =
    ogiMetadata?.prospect_reference?.trim() &&
    ogiMetadata?.instream_status?.trim().toLowerCase() === "completed"
      ? "Prospect created"
      : ogiMetadata?.send_to_broker_description?.trim() ||
        ogiMetadata?.instream_error_message?.trim() ||
        ogiMetadata?.instream_status?.trim() ||
        ogiMetadata?.send_to_broker_status?.trim() ||
        ogiBaseQuote?.state?.trim() ||
        null;
  const ogiSentSuccessfully =
    Boolean(ogiMetadata?.prospect_reference?.trim()) ||
    ogiMetadata?.send_to_broker_successful === true ||
    ogiMetadata?.send_to_broker_status?.trim().toUpperCase() === "S";

  let overallStatus: MarketplacePricingSummary["overallStatus"] = "Pending";
  if (quotedProviderCount === EXPECTED_MARKETPLACE_PROVIDERS.length) {
    overallStatus = "Quoted";
  } else if (quotedProviderCount > 0) {
    overallStatus = "Partial";
  } else if (allExpectedProvidersResolved) {
    overallStatus = "Failed";
  }

  return {
    expectedProviderCount: EXPECTED_MARKETPLACE_PROVIDERS.length,
    quotedProviderCount,
    resolvedProviderCount,
    missingProviders,
    waitingProviders,
    providersWithoutQuotes,
    providerBreakdown,
    graniteHasResponded,
    customerViewReady,
    allExpectedProvidersResolved,
    ogiSentSuccessfully,
    ogiStatus,
    overallStatus,
  };
}

export function formatPricingOgiIndicator(summary: MarketplacePricingSummary): string {
  return `${summary.quotedProviderCount}/${summary.expectedProviderCount} - ${summary.ogiSentSuccessfully ? "Yes" : "No"}`;
}

export function formatPricingOgiTooltip(summary: MarketplacePricingSummary): string {
  const providerLines = summary.providerBreakdown.map((item) => {
    const providerLabel =
      item.provider === "inshur"
        ? "INSHUR"
        : item.provider === "kinetiq"
          ? "Kinetiq"
          : item.provider === "granite"
            ? "Granite"
            : item.provider;

    return `${providerLabel}: ${item.returnedQuote ? "Yes" : "No"}`;
  });

  const ogiSuffix = summary.ogiStatus ? ` (${summary.ogiStatus})` : "";
  return [
    "Pricing providers:",
    ...providerLines,
    `OGI sent: ${summary.ogiSentSuccessfully ? "Yes" : "No"}${ogiSuffix}`,
  ].join("\n");
}

function toTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function latestQuoteActivityAt(
  quote: Pick<LeadQuote, "created_at" | "updated_at" | "base_quotes">,
): string | null {
  const candidates: Array<{ raw: string; timestamp: number }> = [];

  if (quote.updated_at) {
    const timestamp = toTimestamp(quote.updated_at);
    if (timestamp !== null) {
      candidates.push({ raw: quote.updated_at, timestamp });
    }
  }

  if (quote.created_at) {
    const timestamp = toTimestamp(quote.created_at);
    if (timestamp !== null) {
      candidates.push({ raw: quote.created_at, timestamp });
    }
  }

  for (const baseQuote of quote.base_quotes ?? []) {
    for (const value of [baseQuote.updated_at, baseQuote.created_at]) {
      if (!value) continue;
      const timestamp = toTimestamp(value);
      if (timestamp !== null) {
        candidates.push({ raw: value, timestamp });
      }
    }
  }

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => b.timestamp - a.timestamp);
  return candidates[0].raw;
}

function convertInshurPremium(value: number | null | undefined): number {
  const premium = value ?? 0;
  return premium > 10000 ? premium / 100 : premium;
}

function extractOnlineReference(
  detail: BrokerQuoteDetail,
  fallback?: string | null,
): string | undefined {
  const info = detail.info as Record<string, unknown> | null | undefined;
  const reviewLink = typeof info?.ReviewLink === "string" ? info.ReviewLink : undefined;
  const quoteLink = typeof info?.QuoteLink === "string" ? info.QuoteLink : undefined;
  const quoteQuoteLink =
    info?.Quote && typeof info.Quote === "object" && info.Quote !== null
      ? (info.Quote as Record<string, unknown>).quoteLink
      : undefined;

  return reviewLink ||
    quoteLink ||
    (typeof quoteQuoteLink === "string" ? quoteQuoteLink : undefined) ||
    fallback ||
    undefined;
}

function mapGraniteQuotes(baseQuote: BaseQuote): CustomerQuote[] {
  const details = baseQuote.non_broker_quote_details;
  if (!details?.cover) return [];

  const insurer = details.insurer ?? "Granite";

  const variants: Array<{ key: CoverKey; label: CoverType; excess: number }> = [
    {
      key: "comprehensive",
      label: "Comprehensive",
      excess: details.excess?.comprehensive ?? 0,
    },
    {
      key: "tpft",
      label: "Third Party, Fire and Theft",
      excess: details.excess?.tpft ?? 0,
    },
    {
      key: "tp",
      label: "Third Party Only",
      excess: details.excess?.tp ?? 0,
    },
  ];

  return variants
    .map(({ key, label, excess }) => {
      const options = details.cover?.[key];
      if (!options) return null;

      return {
        provider: "granite",
        insurer,
        coverType: label,
        excess,
        compulsoryExcess: excess,
        providerReference: baseQuote.ref ?? undefined,
        onlineReference: baseQuote.online_ref ?? undefined,
        annualOnly: false,
        customerPricing: calculateCustomerPricing("granite", getGraniteAnnualPremium(options)),
        price: { [key]: toCustomerCoverTypePrice("granite", getGraniteAnnualPremium(options)) },
      } satisfies CustomerQuote;
    })
    .filter((quote): quote is Exclude<typeof quote, null> => quote !== null);
}

function mapBrokerQuote(
  baseQuote: BaseQuote,
  detail: BrokerQuoteDetail,
  providerLabel: string,
  annualPremium: number,
  conditionsOverride?: string[],
): CustomerQuote | null {
  if (detail.is_decline || (detail.premium ?? 0) <= 0) return null;

  const coverType = normaliseCoverType(detail.cover_type);
  const coverKey = coverType === "Comprehensive"
    ? "comprehensive"
    : coverType === "Third Party, Fire and Theft"
      ? "tpft"
      : "tp";

  return {
    provider: (baseQuote.provider ?? providerLabel).toLowerCase(),
    insurer: providerLabel,
    coverType,
    isIndicative: (baseQuote.provider ?? "").trim().toLowerCase() === "inshur",
    excess: detail.voluntary_excess ?? 0,
    compulsoryExcess: Number(detail.compulsory_excess?.[0] ?? 0),
    providerReference: detail.uuid ?? baseQuote.ref ?? undefined,
    onlineReference: extractOnlineReference(detail, baseQuote.online_ref),
    expirationDate: detail.expiration_date ?? undefined,
    annualOnly: false,
    endorsements: detail.endorsements ?? [],
    conditions: conditionsOverride ?? detail.conditions ?? [],
    customerPricing: calculateCustomerPricing(
      baseQuote.provider ?? providerLabel,
      annualPremium,
    ),
    price: {
      [coverKey]: toCustomerCoverTypePrice(baseQuote.provider ?? providerLabel, annualPremium),
    },
  };
}

function mapInshurQuotes(baseQuote: BaseQuote): CustomerQuote[] {
  return (baseQuote.broker_quote_details ?? [])
    .map((detail) =>
      mapBrokerQuote(baseQuote, detail, "INSHUR", convertInshurPremium(detail.premium)),
    )
    .filter((quote): quote is CustomerQuote => quote !== null);
}

function mapKinetiqQuotes(baseQuote: BaseQuote): CustomerQuote[] {
  return (baseQuote.broker_quote_details ?? [])
    .map((detail) =>
      mapBrokerQuote(
        baseQuote,
        detail,
        "Kinetiq",
        detail.premium ?? 0,
        sanitizeKinetiqConditions(detail.conditions ?? []),
      ),
    )
    .filter((quote): quote is CustomerQuote => quote !== null);
}

function sanitizeKinetiqConditions(conditions: string[]): string[] {
  const seen = new Set<string>();

  return conditions
    .map((condition) => sanitizeKinetiqCondition(condition))
    .filter((condition): condition is string => Boolean(condition))
    .filter((condition) => {
      const normalized = condition.toLowerCase();
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function sanitizeKinetiqCondition(condition: string): string | null {
  const strippedComments = condition.replace(/<!--[\s\S]*?-->/g, " ");
  const strippedTags = strippedComments.replace(/<[^>]+>/g, " ");
  const decoded = decodeHtmlEntities(strippedTags)
    .replace(/\s+/g, " ")
    .trim();

  if (!decoded) return null;
  if (/^\d+$/.test(decoded)) return null;
  if (/^accelerant\b/i.test(decoded)) return null;

  return decoded;
}

function decodeHtmlEntities(value: string): string {
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }

  return value
    .replace(/&pound;/gi, "£")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'");
}

function mapBaseQuoteToCustomerQuotes(baseQuote: BaseQuote): CustomerQuote[] {
  switch ((baseQuote.provider ?? "").toLowerCase()) {
    case "granite":
      return mapGraniteQuotes(baseQuote);
    case "inshur":
      return mapInshurQuotes(baseQuote);
    case "kinetiq":
      return mapKinetiqQuotes(baseQuote);
    default:
      return [];
  }
}

/** Flattens all provider results into one customer-facing marketplace quote list. */
export function mapLeadQuoteToQuoteReference(
  leadQuote: LeadQuote,
): QuoteReference | null {
  const baseQuotes = [...(leadQuote.base_quotes ?? [])].sort(
    (a, b) => providerSortValue(a.provider) - providerSortValue(b.provider),
  );

  if (baseQuotes.length === 0) return null;

  const quotes = baseQuotes
    .flatMap(mapBaseQuoteToCustomerQuotes)
    .sort((a, b) => comparableAnnualPrice(a) - comparableAnnualPrice(b));
  const pricingSummary = deriveMarketplacePricingSummary(baseQuotes);

  const driver = leadQuote.driver?.[0];
  const vehicle = leadQuote.vehicle?.[0];

  return {
    reference: leadQuote.reference ?? leadQuote.id,
    quoteId: leadQuote.id,
    quotes,
    marketplaceReady: pricingSummary.customerViewReady,
    pricingSummary,
    providerSummaries: baseQuotes.map(toProviderSummary),
    driverInfo: driver
      ? {
          first_name: driver.first_name ?? undefined,
          last_name: driver.last_name ?? undefined,
          email: driver.email ?? undefined,
          telephone: driver.telephone ?? undefined,
          address: driver.address ?? null,
        }
      : undefined,
    vehicleInfo: vehicle
      ? {
          make: vehicle.make ?? undefined,
          model: vehicle.model ?? undefined,
          vrm: vehicle.vrm ?? undefined,
          year: vehicle.year ?? null,
        }
      : undefined,
  };
}

function comparableAnnualPrice(quote: CustomerQuote): number {
  const coverPrice = coverPriceFor(quote);
  if (!coverPrice) return Number.MAX_SAFE_INTEGER;
  return coverPrice.annually > 0 ? coverPrice.annually : Number.MAX_SAFE_INTEGER;
}

/* -------------------------------------------------------------------------- */
/* Price selection                                                             */
/* -------------------------------------------------------------------------- */

/** The pricing block for a quote's own cover type, or null if it has none. */
export function coverPriceFor(quote: CustomerQuote): CoverTypePrice | null {
  const coverKey =
    quote.coverType === "Comprehensive"
      ? "comprehensive"
      : quote.coverType === "Third Party, Fire and Theft"
        ? "tpft"
        : "tp";

  return quote.price[coverKey] ?? null;
}

/** The specific figures to show for the currently selected payment options. */
export function selectedPriceFor(
  quote: CustomerQuote,
  paymentType: PaymentType,
  instalments: Instalments,
): { annually: number } | PriceInfo | null {
  const coverPrice = coverPriceFor(quote);
  if (!coverPrice) return null;

  if (paymentType === "Annually") return { annually: coverPrice.annually };
  return instalments === 10 ? coverPrice.tenMonths : coverPrice.elevenMonths;
}

/**
 * The single number quotes are ranked and filtered by. Returns null when this
 * quote cannot actually be bought on the selected terms, which is what keeps
 * £0 placeholder rows off the screen.
 */
export function comparablePriceFor(
  quote: CustomerQuote,
  paymentType: PaymentType,
  instalments: Instalments,
): number | null {
  const price = selectedPriceFor(quote, paymentType, instalments);
  if (!price) return null;

  const amount = "annually" in price ? price.annually : price.totalPayable;
  return amount > 0 ? amount : null;
}

export function isQuotePurchasable(
  quote: CustomerQuote,
  paymentType: PaymentType,
  instalments: Instalments,
): boolean {
  return comparablePriceFor(quote, paymentType, instalments) !== null;
}
